import { Controller } from "@hotwired/stimulus"

// Owns the UI state of `app/views/shared/_feedback_widget.html.erb`. Submission
// is a plain fetch POST (no Turbo) — we don't need to update the rest of the
// page on success; the widget just collapses itself.
//
// Usage:
//   <div data-controller="feedback-widget"
//        data-feedback-widget-submit-url-value="/path/to/feedback">
//     ...thumbs, chips, textarea...
//   </div>
export default class extends Controller {
  static targets = [
    "form",
    "positiveThumb",
    "negativeThumb",
    "expandedSection",
    "positiveChips",
    "negativeChips",
    "chip",
    "otherSection",
    "textarea",
    "submitButton",
    "successSection",
    "errorMessage",
    "modKey",
    "detailsHint",
  ]

  static values = {
    submitUrl: String,
    dismissDelayMs: { type: Number, default: 2500 },
    // Opt-in (`negative_details:` in the partial): 👎 shows the chips and the text
    // box together, and nothing is sent until "Submit feedback".
    negativeDetails: { type: Boolean, default: false },
    negativePlaceholder: String,
  }

  connect() {
    this.sentiment = null
    this.reason = null
    this.dismissTimer = null
    this.isSubmitting = false
    this.otherPlaceholder = this.hasTextareaTarget ? this.textareaTarget.placeholder : ""

    // ⌘ on macOS, Ctrl elsewhere. Cheap heuristic — `userAgentData.platform`
    // is the future-proof path; `navigator.platform` is the legacy fallback.
    const platform =
      (navigator.userAgentData && navigator.userAgentData.platform) ||
      navigator.platform ||
      ""
    const isMac = /mac/i.test(platform)
    if (!isMac && this.hasModKeyTarget) {
      this.modKeyTarget.textContent = "Ctrl"
    }
  }

  disconnect() {
    this.clearDismissTimer()
  }

  // ── Sentiment selection ──────────────────────────────────────────────
  selectPositive(event) {
    event.preventDefault()
    this.activateSentiment("positive")
  }

  selectNegative(event) {
    event.preventDefault()
    this.activateSentiment("negative")
  }

  activateSentiment(sentiment) {
    if (this.sentiment === sentiment) return

    this.sentiment = sentiment
    this.reason = null

    // Selected state for thumbs and chips is driven by `aria-pressed="true"`
    // via Tailwind's `aria-pressed:` / `group-aria-pressed:` variants in the
    // partial — we only have to toggle the attribute here, no class lists.
    this.positiveThumbTarget.setAttribute("aria-pressed", sentiment === "positive")
    this.negativeThumbTarget.setAttribute("aria-pressed", sentiment === "negative")

    this.expandedSectionTarget.classList.remove("hidden")

    // Show only the matching chip set and reset selection state on every chip.
    this.positiveChipsTarget.classList.toggle("hidden", sentiment !== "positive")
    this.negativeChipsTarget.classList.toggle("hidden", sentiment !== "negative")
    this.chipTargets.forEach((c) => c.setAttribute("aria-pressed", "false"))

    this.otherSectionTarget.classList.add("hidden")
    this.hideError()
    if (this.collectingDetails()) this.showDetails()
  }

  // True while a 👎 in negative-details mode is being filled in.
  collectingDetails() {
    return this.negativeDetailsValue && this.sentiment === "negative"
  }

  showDetails() {
    this.otherSectionTarget.classList.remove("hidden")
    if (this.hasDetailsHintTarget) this.detailsHintTarget.classList.remove("hidden")
    // Keeps text typed for 👎, so toggling 👍/👎 by accident doesn't lose it; text
    // written for 👍 "Other" doesn't carry over.
    if (this.textSentiment !== "negative") this.textareaTarget.value = ""
    this.textareaTarget.placeholder = this.negativePlaceholderValue || this.otherPlaceholder
    this.updateSubmitButton()
  }

  // Typing: remembers which thumb the text was written for (see showDetails).
  textInput() {
    this.textSentiment = this.sentiment
    this.updateSubmitButton()
  }

  // "Other" always needs text; any other chip in negative-details mode is enough alone.
  // Stays disabled while a POST is in flight.
  updateSubmitButton() {
    const hasText = this.textareaTarget.value.trim() !== ""
    const chipAlone = this.collectingDetails() && this.reason && this.reason !== "other"
    this.submitButtonTarget.disabled = this.isSubmitting || !(hasText || chipAlone)
  }

  // ── Chip selection ───────────────────────────────────────────────────
  selectChip(event) {
    event.preventDefault()
    const chip = event.currentTarget
    const sentiment = chip.dataset.feedbackWidgetSentimentParam
    const reason = chip.dataset.feedbackWidgetReasonParam

    if (sentiment !== this.sentiment) return // chip was for a stale sentiment

    this.reason = reason
    this.chipTargets.forEach((c) => {
      if (c.dataset.feedbackWidgetSentimentParam !== sentiment) return
      c.setAttribute("aria-pressed", c === chip ? "true" : "false")
    })

    if (this.collectingDetails()) {
      // Chips just mark a reason here; "Submit feedback" sends it with any text.
      this.updateSubmitButton()
      return
    }

    if (reason === "other") {
      if (this.hasDetailsHintTarget) this.detailsHintTarget.classList.add("hidden")
      this.textareaTarget.placeholder = this.otherPlaceholder
      this.otherSectionTarget.classList.remove("hidden")
      this.textareaTarget.value = ""
      this.submitButtonTarget.disabled = true
      this.textareaTarget.focus()
    } else {
      this.otherSectionTarget.classList.add("hidden")
      this.submit({ sentiment, reason, otherText: null })
    }
  }

  // ── "Other" free-text submit ─────────────────────────────────────────
  otherKeydown(event) {
    // ⌘/Ctrl+Enter submits — handle this first and bail, otherwise the
    // deferred enable-button setTimeout below would re-enable the button
    // while the POST is in flight (we explicitly disable it inside submit()).
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      this.submitOther(event)
      return
    }

    // Only enable the submit button when there's something to send.
    setTimeout(() => this.updateSubmitButton(), 0)
  }

  submitOther(event) {
    if (event) event.preventDefault()
    const text = this.textareaTarget.value.trim()
    if (this.collectingDetails()) {
      if (!text && (!this.reason || this.reason === "other")) return
      this.submit({ sentiment: this.sentiment, reason: this.reason || "other", otherText: text || null })
      return
    }
    if (!text) return
    this.submit({ sentiment: this.sentiment, reason: "other", otherText: text })
  }

  // ── Submission ───────────────────────────────────────────────────────
  // Guarded by `isSubmitting` so that rapid double-clicks (or two chip clicks
  // while the first request is still in flight) can't fire two POSTs and
  // create two Feedback rows for the same result-set.
  async submit({ sentiment, reason, otherText }) {
    if (this.isSubmitting) return
    this.isSubmitting = true
    this.hideError()
    this.submitButtonTarget.disabled = true
    this.setChipsDisabled(true)

    const payload = {
      feedback: {
        sentiment,
        response: { reason, other_text: otherText },
      },
    }

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        let message = "Couldn't save feedback. Try again?"
        try {
          const body = await response.json()
          if (body && Array.isArray(body.errors) && body.errors.length > 0) {
            message = body.errors.join(", ")
          }
        } catch {
          // fall through with the default message
        }
        this.showError(message)
        this.recoverFromFailedSubmit()
        return
      }

      this.showSuccess()
    } catch (e) {
      this.showError("Network error — please try again.")
      this.recoverFromFailedSubmit()
    }
  }

  recoverFromFailedSubmit() {
    this.isSubmitting = false
    this.setChipsDisabled(false)
    this.updateSubmitButton()
  }

  // Chips are <span>s (rendered by `shared/chip`), so the native `disabled`
  // attribute does nothing. Use `aria-disabled` + a `pointer-events-none`
  // class to lock them out while a submit is in flight.
  setChipsDisabled(disabled) {
    this.chipTargets.forEach((c) => {
      c.setAttribute("aria-disabled", disabled ? "true" : "false")
      c.classList.toggle("pointer-events-none", disabled)
      c.classList.toggle("opacity-50", disabled)
    })
  }

  // ── Success / dismiss ────────────────────────────────────────────────
  showSuccess() {
    this.clearDismissTimer()
    this.formTarget.classList.add("hidden")
    this.successSectionTarget.classList.remove("hidden")
    this.dismissTimer = setTimeout(() => this.startDismiss(), this.dismissDelayMsValue)
  }

  startDismiss() {
    // tailwindcss-animate fade-out under `motion-safe:` so the animation is
    // skipped automatically for prefers-reduced-motion users (the element
    // just disappears when we call .remove() below).
    this.element.classList.add(
      "motion-safe:animate-out",
      "motion-safe:fade-out",
      "motion-safe:duration-500"
    )
    setTimeout(() => this.element.remove(), 500)
  }

  clearDismissTimer() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────
  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }

  csrfToken() {
    const el = document.querySelector("meta[name='csrf-token']")
    return el ? el.content : ""
  }
}
